import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useParams } from 'quartz-routing/react-router';
import type { ListingCard as DSListingCard } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/CollectionsRpc';
import { SEARCH_QUERY as PUBLIC_SEARCH_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.dfc.new';
import {
  Filter,
  FilterQuery,
  ListingCard,
} from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc';
import { SEARCH_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc.dfc.new';
import { LISTINGS_PER_PAGE } from '../../constants/searchResults';
import { useAppliedFilters } from '../../filters/hooks/useAppliedFilters';
import { usePersonalizationQuery } from '../../hooks/usePersonalizationQuery';
import { useProductTypes } from '../../hooks/useProductTypeSearchParams';
import { SORT_OPTIONS } from '../components/SortDropdown';
import {
  DATA_TYPE_MAP,
  mapSortForRpc,
  parseAppliedFilterValues,
} from '../utils/searchMappers';
import { useCollectionQuery } from './useCollectionQuery';
import { useGetCollectionBySlugQuery } from './useGetCollectionBySlugQuery';

export { LISTINGS_PER_PAGE };

const SEARCH_TIMEOUT_MS = 10000;

export function useSearchResultsQuery() {
  const { collectionSlug: urlCollectionSlug } = useParams<{
    collectionSlug?: string;
  }>();
  const collectionResult = useCollectionQuery();

  const collectionId = collectionResult.isUmbrellaCollection
    ? undefined
    : collectionResult.data?.id;

  const collectionSlug = collectionResult.isUmbrellaCollection
    ? undefined
    : collectionResult.data?.slug;

  // Umbrella collections have no DS collection of their own and stay on the
  // legacy source, so keep their listing grid off the DS getCollectionBySlug
  // path. Only take the DS path once the collection lookup has resolved cleanly
  // — while it's loading, `isUmbrellaCollection` is still false, so gating on it
  // alone would transiently fire getCollectionBySlug for an umbrella slug.
  const useDSCollectionListings =
    !!urlCollectionSlug &&
    !collectionResult.loading &&
    !collectionResult.error &&
    !collectionResult.isUmbrellaCollection;

  const offset = useOffset();
  const sorts = useSorts();

  const searchResult = usePersonalizationQuery({
    app: SEARCH_QUERY,
    public: PUBLIC_SEARCH_QUERY,
    options: {
      context: { timeout: SEARCH_TIMEOUT_MS },
      skip:
        collectionResult.loading ||
        !!collectionResult.error ||
        useDSCollectionListings,
      variables: {
        filter: useFilter(),
        length: LISTINGS_PER_PAGE,
        offset,
        queryType: useQueryType(),
        search: useSearchParams().get('search'),
        sorts,
      },
    },
  });

  const dsCollectionResult = useGetCollectionBySlugQuery({
    skip: !useDSCollectionListings,
  });

  if (useDSCollectionListings) {
    const dsData = dsCollectionResult.data?.result;
    return {
      loading: dsCollectionResult.loading,
      error: dsCollectionResult.error,
      data: dsData
        ? {
            result: {
              total: dsData.total,
              cards: dsData.items.map(adaptDSListingCard),
              // Collections have no AI answer; the grid reads these to pick its layout.
              aiAnswer: undefined,
              aiRequestId: undefined,
              queryType: undefined,
            },
          }
        : undefined,
      queryType: undefined,
      collectionId: dsData?.entityId,
      collectionSlug: urlCollectionSlug,
    };
  }

  return {
    loading: collectionResult.loading || searchResult.loading,
    error: collectionResult.error || searchResult.error,
    data: searchResult.data,
    queryType: searchResult.data?.result.queryType
      ? String(searchResult.data.result.queryType)
      : undefined,
    collectionId,
    collectionSlug,
  };
}

// The DS and personalization ListingCard are the same mkpl-search model under
// different namespaces; default the personalization-only fields the grid reads.
function adaptDSListingCard(card: DSListingCard): ListingCard {
  return {
    ...card,
    listingName: card.listingName ?? '',
    slug: card.slug ?? '',
    highlights: [],
    recommendationSources: [],
    listingTrellisIcon: undefined,
  } as unknown as ListingCard;
}

function useFilter(): Filter {
  const productTypes = useProductTypes();
  const appliedFilters = useAppliedFilters();

  const filtersByField: Record<string, FilterQuery[]> = {};

  for (const filter of appliedFilters) {
    if (filter.field === 'PRODUCT_TYPE') {
      continue;
    }

    filtersByField[filter.field] = [
      {
        values: parseAppliedFilterValues(filter),
        clause: 'OR',
        negation: false,
        __typename: DATA_TYPE_MAP[filter.dataType],
      } as FilterQuery,
    ];
  }

  filtersByField.PRODUCT_TYPE = [
    {
      values: productTypes,
      clause: 'OR',
      negation: false,
      __typename: DATA_TYPE_MAP.STRING,
    } as FilterQuery,
  ];

  return {
    filterGroups: [{ filtersByField, clause: 'AND', negation: false }],
    clause: 'AND',
    negation: false,
  };
}

function useOffset() {
  const params = useSearchParams();
  const page = Number(params.get('page')) || 1;
  return (page - 1) * LISTINGS_PER_PAGE;
}

function useQueryType() {
  const params = useSearchParams();

  return params.get('queryType', {
    allowedValues: ['ELASTIC_SEARCH', 'VAAS', 'LLM'] as const,
  });
}

function useSorts() {
  const params = useSearchParams();
  const sort = params.get('sort', { allowedValues: Object.keys(SORT_OPTIONS) });
  return mapSortForRpc(sort);
}
